import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { IoArrowBack, IoBag } from 'react-icons/io5'

import Input from '@/components/forms/Input/Input'
import PasswordEyeInput from '@/components/forms/PasswordEyeInput/PasswordEyeInput'
import Button from '@/components/common/Button/Button';
import Spinner from '@/components/common/Spinner/Spinner'
import useAuthStore, { type LoginRequest, type AuthResult } from '@/stores/AuthStore'
import routes from '@/routes/routes'

import './Login.css'

const Login = () => {
    const navigate = useNavigate()
    const { logIn, isLoading, isLogged } = useAuthStore()

    const [formData, setFormData] = useState<LoginRequest>({
        email: '',
        password: '',
    })

    useEffect(() => {
        if (isLogged) {
            navigate(routes.home)
        }
    }, [isLogged])

    const handleFormDataChange = (key: string, value: string) => {
        setFormData((prevState) => ({
            ...prevState,
            [key]: value,
        }))
    }

    const validateForm = (formData: LoginRequest) => {
        const { email, password } = formData
        
        if (!email || !password) {
            return { isValid: false, message: 'Por favor complete todos los campos' }
        }
        
        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
            return { isValid: false, message: 'Por favor ingrese una dirección de correo electrónico válida' }
        }
        
        return { isValid: true, message: '' }
    }

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        const validation = validateForm(formData)
        if (!validation.isValid) {
            toast.error(validation.message)
            return
        }

        try {
            const status: AuthResult = await logIn(formData, () => console.log('2FA required'));

            if (status === 'success') {
                toast.success('¡Inicio de sesión exitoso!')
                navigate(routes.home)
            }

            if (status === 'confirm_email') {
                toast.success('Por favor revise su correo electrónico para verificar su cuenta')
                navigate(routes.verifyEmail)
            }

            return
        } catch (error) {
            toast.error('Error al iniciar sesión. Por favor verifique sus credenciales.')
            console.error('Login error:', error)
        }
    }

    return (
        <div className="auth_page">
            <div className="auth_container">
                <div className="auth_header">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate(routes.home)}
                    >
                        <IoArrowBack size={16} />
                        Volver al Inicio
                    </Button>
                </div>

                <div className="auth_card">
                    <div className="auth_brand">
                        <div className="brand_logo">
                            <IoBag size={32} />
                        </div>
                        <h1>Bienvenido de Vuelta</h1>
                        <p>Inicie sesión en su cuenta Plastitex para continuar</p>
                    </div>

                    <form className="auth_form" onSubmit={handleFormSubmit}>
                        <Input
                            name="email"
                            value={formData.email}
                            setValue={(value) => handleFormDataChange('email', value)}
                            label="Dirección de Correo Electrónico"
                            placeholder="Ingrese su correo electrónico"
                            type="email"
                            required
                        />
                        <PasswordEyeInput
                            name="password"
                            value={formData.password}
                            setValue={(value) => handleFormDataChange('password', value)}
                            label="Contraseña"
                            placeholder="Ingrese su contraseña"
                        />
                        <div className="auth_actions">
                            <button 
                                type="button"
                                className="forgot_link" 
                                onClick={() => navigate(routes.forgotPassword)}
                            >
                                ¿Olvidó su contraseña?
                            </button>
                        </div>

                        <Button
                            variant="primary"
                            size="lg"
                            type="submit"
                            disabled={isLoading}
                            className="auth_submit"
                        >
                            {isLoading ? <Spinner size="sm" variant="secondary" /> : 'Iniciar Sesión'}
                        </Button>
                    </form>

                    <div className="auth_footer">
                        <p>
                            ¿No tiene una cuenta?{' '}
                            <button 
                                type="button"
                                className="auth_link"
                                onClick={() => navigate(routes.register)}
                            >
                                Registrarse
                            </button>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Login
